import { useEffect, useMemo, useState } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, query, limit, doc, addDoc, serverTimestamp, where, updateDoc, getDoc, deleteDoc } from 'firebase/firestore';
import { buildDailySummary } from '../../lib/dailyPayroll';
import { markExpensesCacheDirty } from '../../lib/expensesCache';

const formatCurrency = (value) => `$${Number(value || 0).toLocaleString('es-CO', { minimumFractionDigits: 0 })}`;

const toLocalYMD = (value) => {
  const d = value instanceof Date ? value : (value ? new Date(value) : new Date());
  if (Number.isNaN(d.getTime())) return '';
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const PayrollGenerationView = ({ onBack, currentUser }) => {
  const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);

  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [payrollDate, setPayrollDate] = useState(today);
  const [periodStart, setPeriodStart] = useState(today);
  const [periodEnd, setPeriodEnd] = useState(today);
  const [extras, setExtras] = useState(0);
  const [bonuses, setBonuses] = useState(0);
  const [notes, setNotes] = useState('');
  const [generatedReceipt, setGeneratedReceipt] = useState(null); // preview or last approved

  const [employees, setEmployees] = useState([]);
  const [receipts, setReceipts] = useState([]); // historial
  const [saving, setSaving] = useState(false);
  const [attendanceMap, setAttendanceMap] = useState({});
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceError, setAttendanceError] = useState('');
  const [savingAttendance, setSavingAttendance] = useState({});
  const [adjustedTotalOverride, setAdjustedTotalOverride] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [adjustmentError, setAdjustmentError] = useState('');
  const [payrollSettings, setPayrollSettings] = useState(null);
  const [receiptConfirm, setReceiptConfirm] = useState({ open: false, title: '', message: '', loading: false, receipt: null });

  useEffect(() => {
    let cancelled = false;
    const loadEmployees = async () => {
      try {
        const snap = await getDocs(query(collection(db, 'employees'), limit(300)));
        if (cancelled) return;
        setEmployees(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('No se pudieron cargar empleados', err);
      }
    };
    loadEmployees();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadConfig = async () => {
      try {
        const ref = doc(db, 'payrollSettings', 'global');
        const snap = await getDoc(ref);
        if (!cancelled && snap.exists()) {
          setPayrollSettings(snap.data());
        }
      } catch (err) {
        console.error('No se pudo cargar configuración de nómina', err);
      }
    };
    loadConfig();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadReceipts = async () => {
      try {
        const snap = await getDocs(query(collection(db, 'payrollReceipts'), limit(300)));
        if (cancelled) return;
        setReceipts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('No se pudieron cargar comprobantes de nómina', err);
      }
    };
    loadReceipts();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!selectedEmployee || !periodStart || !periodEnd) {
      setAttendanceMap({});
      return;
    }
    let cancelled = false;
    setAttendanceLoading(true);
    const loadAttendance = async () => {
      try {
        const attendanceQuery = query(
          collection(db, 'employeeAttendance'),
          where('employeeId', '==', selectedEmployee),
          where('date', '>=', periodStart),
          where('date', '<=', periodEnd)
        );
        const snap = await getDocs(attendanceQuery);
        if (cancelled) return;
        const map = {};
        snap.docs.forEach((d) => {
          const data = d.data();
          if (data?.date) {
            map[data.date] = { id: d.id, ...data };
          }
        });
        setAttendanceMap(map);
        setAttendanceError('');
      } catch (err) {
        if (!cancelled) {
          console.error('No se pudo cargar asistencia', err);
          setAttendanceMap({});
          setAttendanceError('No se pudo cargar la asistencia del periodo.');
        }
      } finally {
        if (!cancelled) setAttendanceLoading(false);
      }
    };
    loadAttendance();
    return () => {
      cancelled = true;
    };
  }, [selectedEmployee, periodStart, periodEnd]);

  const currentEmployee = useMemo(
    () => employees.find((emp) => String(emp.id) === String(selectedEmployee)),
    [employees, selectedEmployee]
  );
  const summary = useMemo(() => {
    if (!currentEmployee || !periodStart || !periodEnd) return null;
    return buildDailySummary({
      employee: currentEmployee,
      attendanceMap,
      startDate: periodStart,
      endDate: periodEnd,
      extras: Number(extras || 0),
      bonuses: Number(bonuses || 0),
      settings: payrollSettings,
    });
  }, [currentEmployee, attendanceMap, periodStart, periodEnd, extras, bonuses, payrollSettings]);
  const automaticTotal = summary?.totals?.final ?? 0;
  const finalPreviewTotal =
    adjustedTotalOverride === '' ? automaticTotal : Number(adjustedTotalOverride || 0);

  useEffect(() => {
    setAdjustedTotalOverride('');
    setAdjustmentReason('');
    setAdjustmentError('');
  }, [selectedEmployee, periodStart, periodEnd]);

  const buildReceipt = (status = 'preview') => {
    if (!currentEmployee) {
      alert('Selecciona un empleado.');
      return null;
    }
    if (!periodStart || !periodEnd) {
      alert('Define un periodo (inicio y fin).');
      return null;
    }
    if (new Date(periodEnd) < new Date(periodStart)) {
      alert('La fecha fin debe ser mayor o igual a la fecha inicio.');
      return null;
    }
    if (!summary) {
      alert('Configura la asistencia del periodo para calcular el pago.');
      return null;
    }
    setAdjustmentError('');
    const manualOverride =
      adjustedTotalOverride === '' ? null : Number(adjustedTotalOverride);
    if (manualOverride !== null && Number.isNaN(manualOverride)) {
      alert('El ajuste manual debe ser un número válido.');
      return null;
    }
    if (manualOverride !== null && manualOverride < 0) {
      alert('El ajuste manual no puede ser negativo.');
      return null;
    }
    if (manualOverride !== null && !String(adjustmentReason || '').trim()) {
      setAdjustmentError('Indica un motivo para el ajuste manual.');
      return null;
    }
    const payDate = payrollDate || today;
    const finalTotal = manualOverride !== null ? manualOverride : summary.totals.final;
    const salarySubtotal = Number(summary.totals.salarySubtotal || 0);
    const auxSubtotal = Number(summary.totals.auxTransport || 0);
    const calculatedBase = Number(summary.totals.calculatedTotal || 0);
    const extrasValue = Number(summary.totals.extras || 0);
    const bonusesValue = Number(summary.totals.bonuses || 0);
    const adjustment =
      manualOverride !== null
        ? {
            originalTotal: summary.totals.final,
            adjustedTotal: finalTotal,
            reason: adjustmentReason || '',
            adjustedAt: serverTimestamp(),
            adjustedBy: buildUserMeta(),
          }
        : null;

    return {
      payrollType: 'dailyAttendance',
      employeeId: currentEmployee.id,
      employeeName: `${currentEmployee.name}`,
      employeePosition: currentEmployee.position,
      identification: currentEmployee.identification,
      payDate,
      periodStart,
      periodEnd,
      notes: notes || '',
      createdAt: serverTimestamp(),
      attendanceSnapshot: summary.days,
      totals: {
        ...summary.totals,
        finalWithAdjustment: finalTotal,
      },
      calculatedTotal: calculatedBase,
      extras: extrasValue,
      bonuses: bonusesValue,
      gross: calculatedBase + extrasValue + bonusesValue,
      netSalary: finalTotal,
      devengados: {
        salario: salarySubtotal,
        extras: extrasValue,
        bonificaciones: bonusesValue,
        auxTransporte: auxSubtotal,
      },
      deductions: { salud: 0, pension: 0, fsp: 0, otras: 0, total: 0 },
      employer: { total: 0 },
      adjustment,
      dailyRatesUsed: currentEmployee.dailyRates || null,
      attendanceRange: { start: periodStart, end: periodEnd },
      settingsMode: 'daily',
      status,
    };
  };

  const buildUserMeta = () => {
    if (!currentUser) return null;
    return {
      uid: currentUser.uid || currentUser.id || null,
      email: currentUser.email || null,
      name: currentUser.displayName || currentUser.name || currentUser.username || null,
    };
  };

  const handleAttendanceStatusChange = async (date, status) => {
    if (!selectedEmployee || !date) return;
    setSavingAttendance((prev) => ({ ...prev, [date]: true }));
    try {
      const existing = attendanceMap?.[date];
      const payload = {
        employeeId: selectedEmployee,
        date,
        status,
        updatedAt: serverTimestamp(),
        recordedBy: buildUserMeta(),
      };
      if (existing?.id) {
        await updateDoc(doc(db, 'employeeAttendance', existing.id), payload);
        setAttendanceMap((prev) => ({
          ...prev,
          [date]: { ...existing, ...payload },
        }));
      } else {
        const ref = await addDoc(collection(db, 'employeeAttendance'), { ...payload, createdAt: serverTimestamp() });
        setAttendanceMap((prev) => ({
          ...prev,
          [date]: { id: ref.id, ...payload },
        }));
      }
    } catch (err) {
      console.error('No se pudo actualizar la asistencia', err);
      alert('No se pudo actualizar la asistencia. Intenta nuevamente.');
    } finally {
      setSavingAttendance((prev) => ({ ...prev, [date]: false }));
    }
  };

  const handlePreview = () => {
    const receipt = buildReceipt('preview');
    if (receipt) setGeneratedReceipt(receipt);
  };

  const handleApproveAndSave = async () => {
    const receipt = buildReceipt('aprobado');
    if (!receipt) return;

    // evitar duplicados por empleado + periodo aprobados
    const duplicate = receipts.find(
      (r) =>
        (r.status || 'borrador') === 'aprobado' &&
        String(r.employeeId) === String(receipt.employeeId) &&
        String(r.periodStart) === String(receipt.periodStart) &&
        String(r.periodEnd) === String(receipt.periodEnd)
    );
    if (duplicate) {
      alert('Ya existe un recibo aprobado para este empleado y periodo. Anula/elimina el anterior o ajusta el periodo.');
      return;
    }

    setSaving(true);
    try {
      const ref = await addDoc(collection(db, 'payrollReceipts'), receipt);
      const stored = { id: ref.id, ...receipt };
      let payrollExpenseId = null;

      try {
        const dateStr = receipt.payDate || toLocalYMD(new Date());
        const dateValue = new Date(`${dateStr}T08:00:00`);
        const safeDate = Number.isNaN(dateValue.getTime()) ? new Date() : dateValue;
        const amount = Number(
          receipt.totals?.finalWithAdjustment ??
          receipt.netSalary ??
          receipt.neto ??
          0
        );
        const who = currentUser?.email ?? currentUser?.username ?? currentUser ?? 'nomina';

        const expensePayload = {
          category: 'Nomina',
          amount,
          description: `Pago de nomina - ${receipt.employeeName || 'Empleado'}`,
          date: safeDate,
          dateStr,
          year: dateStr?.slice(0, 4) || '',
          month: dateStr?.slice(0, 7) || '',
          registeredBy: who,
          source: 'payroll',
          payrollReceiptId: ref.id,
          affectsCash: false,
          isRecurring: false,
          recurrencePeriod: 'mensual',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };

        const expenseRef = await addDoc(collection(db, 'gastos'), expensePayload);
        markExpensesCacheDirty('payroll-expense-created');
        payrollExpenseId = expenseRef.id;
        await updateDoc(doc(db, 'payrollReceipts', ref.id), { payrollExpenseId });
      } catch (expenseErr) {
        console.error('No se pudo crear gasto de nomina', expenseErr);
      }

      const storedWithExpense = payrollExpenseId ? { ...stored, payrollExpenseId } : stored;
      setGeneratedReceipt(storedWithExpense);
      setReceipts((prev) => [storedWithExpense, ...prev]);
    } catch (e) {
      console.error(e);
      alert('No se pudo guardar el recibo. Intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  const handleSendEmail = () => {
    if (generatedReceipt) {
      alert(`Recibo de ${generatedReceipt.employeeName} enviado por correo (simulado).`);
    } else {
      alert('Primero genera un recibo.');
    }
  };

  const handleDeleteReceipt = async (receipt) => {
    if (!receipt?.id) return;
    setReceiptConfirm({
      open: true,
      title: 'Eliminar recibo',
      message: `¿Eliminar el recibo de ${receipt.employeeName}? Esta acción no se puede deshacer.`,
      loading: false,
      receipt,
    });
  };

  const renderReceiptHtml = (receipt) => {
    const includeAuxColumn = (receipt?.totals?.auxTransport || 0) > 0;
    const dayRows = (receipt.attendanceSnapshot || [])
      .map(
        (day) => `
        <tr>
          <td style="padding:4px 8px;border-bottom:1px solid #eee;">
            <strong>${day.date}</strong><br/><span style="font-size:11px;color:#666;">${day.dayName || ''}${day.isHoliday ? ` · Festivo - ${day.holidayName || ''}` : ''}</span>
          </td>
          <td style="padding:4px 8px;border-bottom:1px solid #eee;">${day.status === 'present' ? 'Asistio' : 'Falto'}</td>
          <td style="padding:4px 8px;border-bottom:1px solid #eee;">${formatCurrency(day.rate)}</td>
          ${includeAuxColumn ? `<td style="padding:4px 8px;border-bottom:1px solid #eee;">${formatCurrency(day.aux)}</td>` : ''}
          <td style="padding:4px 8px;border-bottom:1px solid #eee;">${formatCurrency(day.paid)}</td>
        </tr>`
      )
      .join('') || `<tr><td colspan="${includeAuxColumn ? 5 : 4}" style="padding:8px;">Sin registros de asistencia.</td></tr>`;
    const adjustmentHtml = receipt.adjustment
      ? `<div class="row"><span>Ajuste:</span><span>${formatCurrency(
          receipt.adjustment.originalTotal || 0
        )} -> ${formatCurrency(receipt.adjustment.adjustedTotal || 0)}</span></div>${
          receipt.adjustment.reason ? `<p class="muted">Motivo: ${receipt.adjustment.reason}</p>` : ''
        }`
      : '';
    return `
      <html>
        <head>
          <title>Recibo de nomina - ${receipt.employeeName}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #111; }
            h1 { text-align: center; margin-bottom: 16px; }
            h2 { margin: 16px 0 8px; }
            .row { display: flex; justify-content: space-between; margin: 4px 0; }
            .muted { color: #555; font-size: 12px; }
            .total { font-size: 20px; font-weight: bold; color: #0a8a3c; }
            .section { border-top: 1px solid #ddd; padding-top: 10px; margin-top: 10px; }
            table { width:100%; border-collapse: collapse; font-size: 13px; }
            th { text-align:left; background:#f8f8f8; padding:6px 8px; }
          </style>
        </head>
        <body>
          <h1>Recibo de nomina</h1>
          <div class="row"><span>Empleado:</span><strong>${receipt.employeeName}</strong></div>
          <div class="row"><span>Identificacion:</span><span>${receipt.identification || ''}</span></div>
          <div class="row"><span>Cargo:</span><span>${receipt.employeePosition || ''}</span></div>
          <div class="row"><span>Periodo:</span><span>${receipt.periodStart} al ${receipt.periodEnd}</span></div>
          <div class="row"><span>Fecha de nomina:</span><span>${receipt.payDate}</span></div>
          <div class="row"><span>Estado:</span><span>${receipt.status || 'borrador'}</span></div>

          <div class="section">
            <h2>Detalle diario</h2>
            <table>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Estado</th>
                  <th>Tarifa</th>
                  ${includeAuxColumn ? '<th>Auxilio</th>' : ''}
                  <th>Pagado</th>
                </tr>
              </thead>
              <tbody>
                ${dayRows}
              </tbody>
            </table>
          </div>

          <div class="section">
            <div class="row"><span>Subtotal tarifas:</span><strong>${formatCurrency(receipt?.totals?.salarySubtotal || 0)}</strong></div>
            ${includeAuxColumn ? `<div class="row"><span>Auxilio transporte:</span><strong>${formatCurrency(receipt?.totals?.auxTransport || 0)}</strong></div>` : ''}
            <div class="row"><span>Subtotal asistencia:</span><strong>${formatCurrency(receipt?.totals?.calculatedTotal || 0)}</strong></div>
            <div class="row"><span>Extras:</span><strong>${formatCurrency(receipt?.totals?.extras || 0)}</strong></div>
            <div class="row"><span>Bonificaciones:</span><strong>${formatCurrency(receipt?.totals?.bonuses || 0)}</strong></div>
            <div class="row"><span>Total automatico:</span><strong>${formatCurrency(receipt?.totals?.final || receipt?.gross || 0)}</strong></div>
            ${adjustmentHtml}
            <div class="row total"><span>Total a pagar:</span><span>${formatCurrency(
              receipt?.totals?.finalWithAdjustment ?? receipt.netSalary ?? receipt.neto ?? 0
            )}</span></div>
          </div>

          ${receipt.notes ? `<p class="section"><strong>Notas:</strong> ${receipt.notes}</p>` : ''}
          <p class="muted">Generado en ${new Date().toLocaleString('es-CO')}</p>
        </body>
      </html>
    `;
  };

  const handleDownloadPDF = (rec = generatedReceipt) => {
    if (!rec) {
      alert('Primero genera un recibo para descargar.');
      return;
    }
    const html = renderReceiptHtml(rec);
    const printWin = window.open('', '_blank');
    if (!printWin) {
      alert('No se pudo abrir la ventana de impresion. Verifica el bloqueador de ventanas emergentes.');
      return;
    }
    printWin.document.write(html);
    printWin.document.close();
    printWin.focus();
    printWin.print();
    printWin.close();
  };

  const historySorted = useMemo(() => {
    return [...receipts].sort((a, b) => {
      const da = new Date(a.payDate || a.createdAt?.toDate?.() || 0).getTime();
      const db = new Date(b.payDate || b.createdAt?.toDate?.() || 0).getTime();
      return db - da;
    });
  }, [receipts]);

  return (
    <div className="p-4 sm:p-6 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-center mb-6 sm:mb-8">
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 hover:text-blue-800 transition duration-200 px-4 py-2 rounded-lg border border-blue-200 hover:border-blue-400 w-full md:w-auto justify-center md:justify-start"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Volver al Menu
        </button>
        <h1 className="text-2xl sm:text-4xl font-extrabold text-gray-900 drop-shadow-sm text-center">
          Generacion de comprobantes de nomina
        </h1>
        <div className="hidden md:block w-32"></div>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 w-full max-w-4xl mx-auto">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6 text-center">Generar recibo de pago</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Empleado</label>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
            >
              <option value="">Seleccionar empleado...</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.name} ({emp.position})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de nomina</label>
            <input
              type="date"
              value={payrollDate}
              onChange={(e) => setPayrollDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Periodo inicio</label>
            <input
              type="date"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Periodo fin</label>
            <input
              type="date"
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Extras / recargos ($)</label>
            <input
              type="number"
              min="0"
              step="1000"
              value={extras}
              onChange={(e) => setExtras(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
              placeholder="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bonificaciones ($)</label>
            <input
              type="number"
              min="0"
              step="1000"
              value={bonuses}
              onChange={(e) => setBonuses(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
              placeholder="0"
            />
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Notas (opcional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
            placeholder="Observaciones internas, formula de pago, etc."
          />
        </div>
        <div className="mb-8 border border-gray-200 rounded-2xl bg-gray-50 p-5 shadow-inner">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
            <div>
              <h3 className="text-xl font-semibold text-gray-800">Asistencia del periodo</h3>
              <p className="text-sm text-gray-500">Solo se paga al empleado cuando existe asistencia.</p>
              <p className="text-xs text-blue-600 mt-1">Los festivos nacionales de Colombia se detectan automáticamente y usan la tarifa Domingo / Festivo.</p>
            </div>
            {summary && (
              <div className="flex gap-3 text-xs uppercase tracking-wide">
                <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 font-semibold">
                  Asistidos: {summary.totals.daysPresent}
                </span>
                <span className="px-3 py-1 rounded-full bg-rose-100 text-rose-700 font-semibold">
                  Faltas: {summary.totals.daysAbsent}
                </span>
              </div>
            )}
          </div>
          {attendanceError && <p className="text-sm text-red-600 mb-2">{attendanceError}</p>}
          {attendanceLoading ? (
            <p className="text-sm text-gray-500">Cargando asistencia...</p>
          ) : summary ? (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm bg-white">
                  <thead>
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">Fecha</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">Estado</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">Tarifa</th>
                      {summary?.totals?.auxTransport > 0 && (
                        <th className="px-3 py-2 text-left font-semibold text-gray-600">Auxilio</th>
                      )}
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">Pagado</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {summary.days.map((day) => {
                      const isSaving = !!savingAttendance[day.date];
                      return (
                        <tr key={day.date}>
                          <td className="px-3 py-2">
                            <p className="font-medium text-gray-900">{day.date}</p>
                            <p className="text-xs text-gray-500">{day.dayName}</p>
                            {day.isHoliday && (
                              <p className="text-xs font-semibold text-amber-700 mt-1" title={day.holidaySource || ''}>
                                Festivo · {day.holidayName}
                              </p>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                day.status === 'present' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
                              }`}
                            >
                              {day.status === 'present' ? 'Asistio' : 'Falto'}
                            </span>
                          </td>
                          <td className="px-3 py-2">{formatCurrency(day.rate)}</td>
                          {summary?.totals?.auxTransport > 0 && (
                            <td className="px-3 py-2">{formatCurrency(day.aux)}</td>
                          )}
                          <td className="px-3 py-2 font-semibold text-gray-800">{formatCurrency(day.paid)}</td>
                          <td className="px-3 py-2">
                            <div className="flex gap-2">
                              <button
                                type="button"
                                disabled={isSaving}
                                onClick={() => handleAttendanceStatusChange(day.date, 'present')}
                                className={`px-3 py-1 rounded-lg text-xs font-semibold ${
                                  day.status === 'present'
                                    ? 'bg-emerald-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                                }`}
                              >
                                {isSaving && day.status !== 'present' ? '...' : 'Asistio'}
                              </button>
                              <button
                                type="button"
                                disabled={isSaving}
                                onClick={() => handleAttendanceStatusChange(day.date, 'absent')}
                                className={`px-3 py-1 rounded-lg text-xs font-semibold ${
                                  day.status === 'absent'
                                    ? 'bg-rose-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-rose-50 hover:text-rose-700'
                                }`}
                              >
                                {isSaving && day.status !== 'absent' ? '...' : 'Falto'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <div className="p-3 rounded-lg bg-white border">
                  <p className="text-gray-500">Días con asistencia</p>
                  <p className="text-xl font-bold text-gray-900">{summary.totals.daysPresent}</p>
                </div>
                <div className="p-3 rounded-lg bg-white border">
                  <p className="text-gray-500">Días sin pago</p>
                  <p className="text-xl font-bold text-gray-900">{summary.totals.daysAbsent}</p>
                </div>
              <div className="p-3 rounded-lg bg-white border">
                <p className="text-gray-500">Total (tarifa + aux.)</p>
                <p className="text-xl font-bold text-gray-900">{formatCurrency(summary.totals.calculatedTotal)}</p>
              </div>
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500">Selecciona un empleado y define el periodo para registrar su asistencia.</p>
          )}
        </div>

        <div className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-5 border border-gray-200 rounded-2xl shadow-sm bg-white space-y-2">
            <h4 className="text-lg font-semibold text-gray-800">Resumen del cálculo</h4>
            <p className="flex justify-between text-sm text-gray-600">
              <span>Subtotal tarifas:</span>
              <strong>{formatCurrency(summary?.totals?.salarySubtotal || 0)}</strong>
            </p>
            {summary?.totals?.auxTransport > 0 && (
              <p className="flex justify-between text-sm text-gray-600">
                <span>Auxilio transporte aplicado:</span>
                <strong>{formatCurrency(summary?.totals?.auxTransport || 0)}</strong>
              </p>
            )}
            <p className={`flex justify-between text-sm text-gray-600 ${summary?.totals?.auxTransport > 0 ? 'border-t pt-2' : ''}`}>
              <span>Subtotal asistencia (tarifa + aux.):</span>
              <strong>{formatCurrency(summary?.totals?.calculatedTotal || 0)}</strong>
            </p>
            <p className="flex justify-between text-sm text-gray-600">
              <span>Extras:</span>
              <strong>{formatCurrency(summary?.totals?.extras || 0)}</strong>
            </p>
            <p className="flex justify-between text-sm text-gray-600">
              <span>Bonificaciones:</span>
              <strong>{formatCurrency(summary?.totals?.bonuses || 0)}</strong>
            </p>
            <p className="flex justify-between text-lg font-bold text-gray-900 border-t pt-2">
              <span>Total automatico:</span>
              <span>{formatCurrency(automaticTotal)}</span>
            </p>
            <p className="text-xs text-gray-500">Tarifas tomadas del perfil del empleado.</p>
            <p className="text-xs text-gray-500">Pagos diarios no incluyen auxilio de transporte.</p>
          </div>
          <div className="p-5 border border-gray-200 rounded-2xl shadow-sm bg-white space-y-3">
            <h4 className="text-lg font-semibold text-gray-800">Ajuste manual (opcional)</h4>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Valor final a pagar</label>
              <input
                type="number"
                min="0"
                value={adjustedTotalOverride}
                onChange={(e) => setAdjustedTotalOverride(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Si lo dejas vacío se usa el cálculo automatico"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Motivo del ajuste</label>
              <textarea
                rows={2}
                value={adjustmentReason}
                onChange={(e) => {
                  setAdjustmentReason(e.target.value);
                  if (adjustmentError && e.target.value.trim()) setAdjustmentError('');
                }}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                  adjustmentError ? 'border-rose-400' : 'border-gray-300'
                }`}
                placeholder="Ejemplo: descuento por retraso"
              />
            </div>
            <p className="text-xs text-gray-500">
              El motivo es obligatorio cuando se ingresa un valor manual. Total preliminar:{' '}
              <strong className="text-blue-600">{formatCurrency(Number.isNaN(finalPreviewTotal) ? 0 : finalPreviewTotal)}</strong>
            </p>
            {adjustmentError && (
              <p className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                {adjustmentError}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-center md:space-x-4 space-y-3 md:space-y-0">
          <button
            onClick={handlePreview}
            className="px-6 py-3 bg-gray-100 text-gray-800 rounded-lg text-lg font-semibold hover:bg-gray-200 border"
          >
            Previsualizar (no guarda)
          </button>
          <button
            onClick={handleApproveAndSave}
            disabled={saving}
            className={`px-8 py-3 rounded-lg text-lg font-semibold text-white ${saving ? 'bg-blue-300' : 'bg-blue-600 hover:bg-blue-700'} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200`}
          >
            {saving ? 'Guardando...' : 'Aprobar y guardar'}
          </button>
        </div>

        {generatedReceipt && (
          <div className="mt-10 p-6 border border-gray-200 rounded-lg shadow-md bg-gray-50">
            <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">Recibo de nomina</h3>
            <p className="text-lg mb-2">
              <strong>Empleado:</strong> {generatedReceipt.employeeName}
            </p>
            <p className="text-lg mb-2">
              <strong>Identificacion:</strong> {generatedReceipt.identification}
            </p>
            <p className="text-lg mb-2">
              <strong>Cargo:</strong> {generatedReceipt.employeePosition}
            </p>
            <p className="text-lg mb-2">
              <strong>Periodo:</strong> {generatedReceipt.periodStart} al {generatedReceipt.periodEnd}
            </p>
            <p className="text-lg mb-2">
              <strong>Fecha de nomina:</strong> {generatedReceipt.payDate}
            </p>
            <p className="text-sm mb-2 text-gray-600">
              <strong>Estado:</strong> {generatedReceipt.status === 'aprobado' ? 'Aprobado / listo para pago' : 'Borrador (no suma en reportes)'}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 my-4">
              <div className="p-3 rounded-lg bg-white border">
                <p className="text-gray-500 text-sm">Días asistidos</p>
                <p className="text-2xl font-bold text-gray-900">{generatedReceipt?.totals?.daysPresent ?? 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-white border">
                <p className="text-gray-500 text-sm">Días sin pago</p>
                <p className="text-2xl font-bold text-gray-900">{generatedReceipt?.totals?.daysAbsent ?? 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-white border">
                <p className="text-gray-500 text-sm">Total (tarifa + aux.)</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(generatedReceipt?.totals?.calculatedTotal || 0)}</p>
              </div>
            </div>

            <div className="border border-dashed border-gray-300 rounded-xl p-4 bg-white">
              <h4 className="text-lg font-semibold text-gray-800 mb-2">Detalle diario</h4>
              <div className="max-h-64 overflow-y-auto border rounded-lg">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">Fecha</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">Estado</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">Tarifa</th>
                      {generatedReceipt?.totals?.auxTransport > 0 && (
                        <th className="px-3 py-2 text-left font-semibold text-gray-600">Auxilio</th>
                      )}
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">Pagado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(generatedReceipt.attendanceSnapshot || []).map((day) => (
                      <tr key={day.date}>
                        <td className="px-3 py-2">
                          <p className="font-medium text-gray-900">{day.date}</p>
                          <p className="text-xs text-gray-500">{day.dayName}</p>
                          {day.isHoliday && (
                            <p className="text-xs font-semibold text-amber-700 mt-1">Festivo · {day.holidayName}</p>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              day.status === 'present' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
                            }`}
                          >
                            {day.status === 'present' ? 'Asistio' : 'Falto'}
                          </span>
                        </td>
                        <td className="px-3 py-2">{formatCurrency(day.rate)}</td>
                        {generatedReceipt?.totals?.auxTransport > 0 && (
                          <td className="px-3 py-2">{formatCurrency(day.aux)}</td>
                        )}
                        <td className="px-3 py-2 font-semibold text-gray-800">{formatCurrency(day.paid)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="border-t border-gray-300 my-4 pt-4 space-y-1">
              <p className="flex justify-between text-lg">
                <span>Tarifas acumuladas:</span> <strong>{formatCurrency(generatedReceipt?.totals?.salarySubtotal || 0)}</strong>
              </p>
              <p className="flex justify-between text-lg">
                <span>Auxilio transporte:</span> <strong>{formatCurrency(generatedReceipt?.totals?.auxTransport || 0)}</strong>
              </p>
              <p className="flex justify-between text-lg">
                <span>Subtotal asistencia:</span> <strong>{formatCurrency(generatedReceipt?.totals?.calculatedTotal || 0)}</strong>
              </p>
              <p className="flex justify-between text-lg">
                <span>Extras:</span> <strong>{formatCurrency(generatedReceipt?.totals?.extras || 0)}</strong>
              </p>
              <p className="flex justify-between text-lg">
                <span>Bonificaciones:</span> <strong>{formatCurrency(generatedReceipt?.totals?.bonuses || 0)}</strong>
              </p>
              <p className="flex justify-between text-lg font-semibold text-gray-900 border-t pt-2">
                <span>Total automatico:</span> <span>{formatCurrency(generatedReceipt?.totals?.final || generatedReceipt?.gross || 0)}</span>
              </p>
              {generatedReceipt?.adjustment && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-900">
                  <p>
                    <strong>Ajuste aplicado:</strong> {formatCurrency(generatedReceipt.adjustment.originalTotal)} ->{' '}
                    {formatCurrency(generatedReceipt.adjustment.adjustedTotal)}
                  </p>
                  {generatedReceipt.adjustment.reason && <p>Motivo: {generatedReceipt.adjustment.reason}</p>}
                </div>
              )}
            </div>

            <p className="flex justify-between text-2xl font-bold text-green-700">
              <span>Total a pagar:</span>{' '}
              <span>{formatCurrency(generatedReceipt?.totals?.finalWithAdjustment ?? generatedReceipt.netSalary ?? generatedReceipt.neto)}</span>
            </p>

            {generatedReceipt.notes && (
              <div className="mt-4 text-sm text-gray-700">
                <strong>Notas:</strong> {generatedReceipt.notes}
              </div>
            )}

            <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
              <button
                onClick={handleSendEmail}
                className="px-4 sm:px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition duration-200 flex items-center w-full sm:w-auto justify-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                Enviar por correo
              </button>
              <button
                onClick={() => handleDownloadPDF(generatedReceipt)}
                className="px-4 sm:px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-200 flex items-center w-full sm:w-auto justify-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2m7 0V5a2 2 0 00-2-2H7a2 2 0 00-2 2v6m7 0h2"
                  />
                </svg>
                Descargar PDF
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6 max-w-4xl mx-auto mt-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Historial de recibos</h2>
        <p className="text-sm text-gray-600 mb-4">Solo los "aprobado" se incluyen en los reportes. Puedes reimprimir desde aqui.</p>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Empleado</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Periodo</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Fecha nomina</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Neto</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Estado</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {historySorted.map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-2 text-sm text-gray-800">{r.employeeName}</td>
                  <td className="px-4 py-2 text-sm text-gray-600">{r.periodStart} - {r.periodEnd}</td>
                  <td className="px-4 py-2 text-sm text-gray-600">{r.payDate}</td>
                  <td className="px-4 py-2 text-sm text-gray-800">{formatCurrency(r?.totals?.finalWithAdjustment ?? r?.totals?.final ?? r.netSalary ?? r.neto)}</td>
                  <td className="px-4 py-2 text-sm text-gray-600">{r.status || 'borrador'}</td>
                  <td className="px-4 py-2 text-sm space-x-4">
                    <button
                      onClick={() => handleDownloadPDF(r)}
                      className="text-blue-600 hover:text-blue-800 font-semibold"
                    >
                      Reimprimir
                    </button>
                    <button
                      onClick={() => handleDeleteReceipt(r)}
                      className="text-red-600 hover:text-red-800 font-semibold"
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
              {historySorted.length === 0 && (
                <tr>
                  <td className="px-4 py-4 text-sm text-gray-500" colSpan={6}>Sin recibos generados.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {receiptConfirm.open && (
        <div className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-md p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center">
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M12 18.5a6.5 6.5 0 110-13 6.5 6.5 0 010 13z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{receiptConfirm.title}</h3>
                <p className="text-sm text-gray-600">{receiptConfirm.message}</p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                disabled={receiptConfirm.loading}
                onClick={() => setReceiptConfirm({ open: false, title: '', message: '', loading: false, receipt: null })}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                disabled={receiptConfirm.loading}
                onClick={async () => {
                  setReceiptConfirm((prev) => ({ ...prev, loading: true }));
                  try {
                    const payrollExpenseId = receiptConfirm.receipt?.payrollExpenseId;
                    if (payrollExpenseId) {
                      try {
                        await deleteDoc(doc(db, 'gastos', payrollExpenseId));
                        markExpensesCacheDirty('payroll-expense-deleted');
                      } catch (expenseErr) {
                        console.warn('No se pudo eliminar gasto de nomina vinculado', expenseErr);
                      }
                    }
                    await deleteDoc(doc(db, 'payrollReceipts', receiptConfirm.receipt.id));
                    setReceipts((prev) => prev.filter((r) => r.id !== receiptConfirm.receipt.id));
                    if (generatedReceipt?.id === receiptConfirm.receipt.id) {
                      setGeneratedReceipt(null);
                    }
                  } catch (err) {
                    console.error('No se pudo eliminar el recibo', err);
                    alert('No se pudo eliminar el recibo. Intenta de nuevo.');
                  } finally {
                    setReceiptConfirm({ open: false, title: '', message: '', loading: false, receipt: null });
                  }
                }}
                className="px-4 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-60"
              >
                {receiptConfirm.loading ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollGenerationView;


